/**
 * Report / chat table: sortable headers, "Manage Columns", comparison cells ("Previous: $x / +$y (+z%)")
 * when rows carry previous values, creative + platform cells, and pagination ("Showing 1-10 of 20").
 */
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/cn";
import { Checkbox } from "../../ui/Form";
import { platformLabel, PlatformLogo } from "../../ui/Logos";
import { PopoverPanel, usePopover } from "../../ui/Overlay";
import { cellParts, columnLabel, deltaDirection, DIRECTION_CLASS, formatDelta, formatDeltaPct, formatValue, normalizeFormat, sortValue, type CellFormat } from "./fmt";
import { safeMediaHref } from "@/lib/safe-url";

export interface TableColumn {
  key: string;
  label: string;
  format?: string;
}

export function ReportTable({
  title,
  columns,
  rows,
  comparisonLabel,
  pageSize = 10,
  className,
  bare,
}: {
  title?: string;
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  comparisonLabel?: string;
  pageSize?: number;
  className?: string;
  /** No outer card (inside chat messages). */
  bare?: boolean;
}) {
  const cols = useMemo(() => columns.map((c) => ({ ...c, fmt: normalizeFormat(c.format, c.key) })), [columns]);
  const [hidden, setHidden] = useState<string[]>([]);
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  const [page, setPage] = useState(0);
  const manage = usePopover();
  const visible = cols.filter((c) => !hidden.includes(c.key));

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const out = [...rows];
    out.sort((a, b) => {
      const va = sortValue(a[sort.key]);
      const vb = sortValue(b[sort.key]);
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, sort]);

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cur = Math.min(page, pages - 1);
  const slice = sorted.slice(cur * pageSize, cur * pageSize + pageSize);

  const toggleSort = (key: string) => {
    setPage(0);
    setSort((s) => (!s || s.key !== key ? { key, dir: "desc" } : s.dir === "desc" ? { key, dir: "asc" } : null));
  };

  return (
    <div className={cn(!bare && "rounded-2xl border border-line bg-surface-2/60 p-4", className)}>
      {(title || comparisonLabel || cols.length > 2) && (
        <div className="mb-3 flex items-start justify-between gap-3 px-1">
          <div className="min-w-0">
            {title && <h3 className="text-[16px] font-semibold tracking-[-0.01em]">{title}</h3>}
            {comparisonLabel && <div className="mt-0.5 text-[12px] text-muted">{comparisonLabel}</div>}
          </div>
          {cols.length > 2 && (
            <>
              <button
                type="button"
                ref={(el) => {
                  manage.anchorRef.current = el;
                }}
                onClick={manage.toggle}
                className="shrink-0 text-[12.5px] font-medium text-link hover:text-lavender"
                aria-expanded={manage.open}
              >
                Manage Columns
              </button>
              <PopoverPanel open={manage.open} onClose={manage.close} anchorRef={manage.anchorRef} align="end" className="max-h-[320px] w-56 overflow-y-auto p-2 scrollbar-thin">
                {cols.map((c) => (
                  <div key={c.key} className="rounded-lg px-2 py-1.5 hover:bg-hover">
                    <Checkbox
                      checked={!hidden.includes(c.key)}
                      disabled={!hidden.includes(c.key) && visible.length <= 1}
                      onChange={(on) => setHidden((h) => (on ? h.filter((k) => k !== c.key) : [...h, c.key]))}
                      label={columnLabel(c)}
                    />
                  </div>
                ))}
              </PopoverPanel>
            </>
          )}
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-line bg-[#141416] scrollbar-thin">
        <table className="w-full min-w-max border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-line">
              {visible.map((c, i) => {
                const active = sort?.key === c.key;
                return (
                  <th key={c.key} scope="col" className={cn("px-4 py-3 text-left font-medium text-muted", i < visible.length - 1 && "border-r border-line/60")} aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}>
                    <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1.5 whitespace-nowrap hover:text-fg">
                      {columnLabel(c)}
                      {active ? sort!.dir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" /> : <ArrowUpDown className="size-3 opacity-50" />}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {slice.map((r, ri) => (
              <tr key={ri} className="border-b border-line/50 last:border-0 hover:bg-white/[0.015]">
                {visible.map((c) => (
                  <td key={c.key} className="max-w-[320px] px-4 py-3.5 align-top">
                    <Cell row={r} col={c} />
                  </td>
                ))}
              </tr>
            ))}
            {!slice.length && (
              <tr>
                <td colSpan={visible.length} className="px-4 py-8 text-center text-muted">
                  No rows
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {sorted.length > pageSize && (
        <div className="mt-3 flex items-center justify-between gap-3 px-1 text-[12.5px] text-muted">
          <span className="tnum">
            Showing {cur * pageSize + 1}-{Math.min(sorted.length, (cur + 1) * pageSize)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage(cur - 1)} disabled={cur === 0} aria-label="Previous page" className="grid size-8 place-items-center rounded-md hover:bg-hover disabled:opacity-30">
              <ChevronLeft className="size-4" />
            </button>
            {Array.from({ length: pages }).map((_, i) =>
              pages > 7 && i > 1 && i < pages - 2 && Math.abs(i - cur) > 1 ? (
                i === 2 || i === pages - 3 ? (
                  <span key={i} className="px-1">
                    …
                  </span>
                ) : null
              ) : (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  aria-current={i === cur ? "page" : undefined}
                  className={cn("grid size-8 place-items-center rounded-md text-[13px] tnum", i === cur ? "bg-white font-semibold text-black" : "hover:bg-hover hover:text-fg")}
                >
                  {i + 1}
                </button>
              ),
            )}
            <button type="button" onClick={() => setPage(cur + 1)} disabled={cur >= pages - 1} aria-label="Next page" className="grid size-8 place-items-center rounded-md hover:bg-hover disabled:opacity-30">
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Cell({ row, col }: { row: Record<string, unknown>; col: TableColumn & { fmt: CellFormat } }) {
  const parts = cellParts(row, col.key);
  if (col.fmt === "platform") {
    const p = String(parts.value ?? "");
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-fg/90">
        <PlatformLogo platform={p} className="size-4" />
        {p ? p.toLowerCase() : "—"}
      </span>
    );
  }
  if (col.fmt === "creative") return <CreativeCell row={row} value={parts.value} />;
  const main = formatValue(parts.value, col.fmt);
  if (!parts.hasComparison) {
    return <span className={cn("tnum", col.fmt === "text" ? "text-fg/85" : "font-semibold text-fg")}>{main}</span>;
  }
  const dir = deltaDirection(parts.delta, { key: col.key });
  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap tnum">
      <span className="font-semibold text-fg">{main}</span>
      <span className="text-[12px] text-muted">Previous: {formatValue(parts.prev, col.fmt)}</span>
      {parts.delta !== null && (
        <span className={cn("text-[12px]", DIRECTION_CLASS[dir])}>
          {formatDelta(parts.delta, col.fmt)}
          {parts.deltaPct !== null ? ` (${formatDeltaPct(parts.deltaPct)})` : ""}
        </span>
      )}
    </div>
  );
}

function CreativeCell({ row, value }: { row: Record<string, unknown>; value: unknown }) {
  const o = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const name = (typeof value === "string" ? value : (o.name as string)) ?? (row.creativeName as string) ?? "—";
  const thumb = safeMediaHref(o.thumbnailUrl ?? o.thumbnail ?? row.thumbnailUrl ?? row.thumbnail ?? row.creativeThumbnail);
  const platform = (o.platform ?? row.platform) as string | undefined;
  const sub = (o.campaign ?? o.campaignName ?? row.campaignName ?? row.campaign) as string | undefined;
  return (
    <div className="flex min-w-[200px] items-center gap-2.5">
      <div className="size-10 shrink-0 overflow-hidden rounded-md border border-line bg-[#1a1a1c]">{thumb ? <img src={thumb} alt="" loading="lazy" className="size-full object-cover" /> : null}</div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-[11.5px] text-muted">
          {platform && <PlatformLogo platform={platform} className="size-3.5" />}
          {platform ? platformLabel(platform) : null}
        </div>
        <div className="truncate font-medium text-fg">{name}</div>
        {sub && <div className="truncate text-[11.5px] text-muted">{sub}</div>}
      </div>
    </div>
  );
}

/** Tolerant table attachment → columns/rows (columns may be strings; rows objects or arrays). */
export function normalizeTable(data: Record<string, unknown> | undefined): { title?: string; columns: TableColumn[]; rows: Record<string, unknown>[]; comparisonLabel?: string } | null {
  if (!data) return null;
  const rawRows = Array.isArray(data.rows) ? data.rows : Array.isArray(data.items) ? data.items : Array.isArray(data.data) ? data.data : [];
  let columns: TableColumn[] = (Array.isArray(data.columns) ? data.columns : [])
    .map((c, i): TableColumn | null => {
      if (typeof c === "string") return { key: c, label: c };
      if (c && typeof c === "object") {
        const o = c as Record<string, unknown>;
        const key = String(o.key ?? o.id ?? o.field ?? i);
        return { key, label: String(o.label ?? o.title ?? o.header ?? key), format: typeof o.format === "string" ? o.format : undefined };
      }
      return null;
    })
    .filter((c): c is TableColumn => !!c);
  const rows = rawRows.map((r) => {
    if (Array.isArray(r)) {
      const o: Record<string, unknown> = {};
      r.forEach((v, i) => (o[columns[i]?.key ?? String(i)] = v));
      return o;
    }
    return (r && typeof r === "object" ? r : { value: r }) as Record<string, unknown>;
  });
  if (!columns.length && rows[0]) columns = Object.keys(rows[0]).map((k) => ({ key: k, label: k }));
  if (!columns.length) return null;
  return { title: typeof data.title === "string" ? data.title : undefined, columns, rows, comparisonLabel: typeof data.comparisonLabel === "string" ? data.comparisonLabel : undefined };
}
